import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { getTemplates, saveTemplate, QuestionnaireTemplate } from "../../utils/api/endpoints/questionnaire";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { ScopingFormBuilder } from "../../components/sales/ScopingFormBuilder";
import { createDefaultSchema, validateScopingSchema } from "../../components/sales/scoping-schema";
import { useScopingSchemaEditor } from "../../components/sales/useScopingSchemaEditor";
import TemplateDevAutofill from "./TemplateDevAutofill";
import {
  SalesPageShell,
  SalesPageHeader,
  SalesPanel,
  SalesLoading,
  SalesButton,
  SalesLinkButton,
  salesTokens
} from "../../components/sales/sales-ui";
import {
  Plus,
  Save,
  Settings,
  ClipboardCheck,
  ClipboardCopy,
  UploadCloud,
  CheckCircle,
  AlertCircle,
  FileJson,
  Download,
  X,
  Link2
} from "lucide-react";

// ── Sample reference JSON structure containing all scoping question types ──
const SAMPLE_TEMPLATE_JSON = {
  sections: [
    {
      id: "section_general",
      title: "1. Organization Details",
      description: "Basic company contact information and project stakeholders.",
      questions: [
        {
          qid: "company_name",
          label: "Company legal name",
          type: "short_text",
          required: true
        },
        {
          qid: "contact_email",
          label: "Primary contact email address",
          type: "email",
          required: true
        },
        {
          qid: "project_description",
          label: "Provide a brief description of your system architecture",
          type: "long_text",
          required: false
        }
      ]
    },
    {
      id: "section_technical_scoping",
      title: "2. Technical Environments & Governance",
      description: "Scope sizes, compliance goals, and target architectures.",
      questions: [
        {
          qid: "compliance_driven",
          label: "Is this assessment driven by a compliance framework?",
          type: "yes_no",
          required: true
        },
        {
          qid: "scale_tier",
          label: "Scale footprint of targets in-scope",
          type: "dropdown",
          required: true,
          options: [
            { value: "Startup", label: "Startup (1-5 targets, single region)" },
            { value: "Growing", label: "Mid-Market (6-20 targets, multi-region)" },
            { value: "Enterprise", label: "Enterprise (21+ targets, hybrid infrastructure)" }
          ]
        },
        {
          qid: "deployment_platforms",
          label: "Select your hosting and cloud deployment environments",
          type: "multi_choice",
          required: false,
          options: [
            { value: "aws", label: "Amazon Web Services (AWS)", flags: ["cloud"] },
            { value: "azure", label: "Microsoft Azure", flags: ["cloud"] },
            { value: "gcp", label: "Google Cloud Platform (GCP)", flags: ["cloud"] },
            { value: "on_prem", label: "On-Premises / Private DC", flags: ["legacy"] }
          ]
        },
        {
          qid: "scoping_methodologies",
          label: "Configure target counts, APIs, and mobile platforms",
          type: "target_builder",
          required: true
        }
      ]
    }
  ]
};

const QuestionnaireTemplatesPage: React.FC = () => {
  usePageTitle("Manage Questionnaires");

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<{ id?: number; name?: string } | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  // ── Import JSON Wizard Modal States ──
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importJsonText, setImportJsonText] = useState("");
  const [validationStatus, setValidationStatus] = useState<"empty" | "invalid_json" | "invalid_schema" | "valid">("empty");
  const [syntaxError, setSyntaxError] = useState<string | null>(null);
  const [schemaErrors, setSchemaErrors] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const editor = useScopingSchemaEditor(null);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const list = await getTemplates();
      setTemplates(list);
    } catch (err) {
      console.error("Failed to load templates:", err);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleStartEdit = (tpl: QuestionnaireTemplate) => {
    setEditingTemplate(tpl);
    setTemplateName(tpl.name);
    setSaveErrors([]);
    try {
      editor.resetEditor(JSON.parse(tpl.schema), 0);
    } catch {
      editor.resetEditor(createDefaultSchema(), 0);
    }
  };

  const handleStartCreate = () => {
    setEditingTemplate({ id: undefined });
    setTemplateName("New Questionnaire");
    setSaveErrors([]);
    editor.resetEditor(createDefaultSchema(), 0);
  };

  const handleCancel = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setSaveErrors([]);
    editor.resetEditor(null, 0);
  };

  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);

  const handleSubmitAttempt = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const errors: string[] = [];
    if (!templateName.trim()) {
      errors.push("Template name is required.");
    }
    errors.push(...editor.validate());

    if (errors.length) {
      setSaveErrors(errors);
      toast.error("Fix validation issues before saving");
      return;
    }

    setShowConfirmSaveModal(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmSaveModal(false);
    const loader = toast.loading("Saving template…");
    try {
      setLoading(true);
      await saveTemplate({
        id: editingTemplate?.id,
        name: templateName.trim(),
        schema: editor.schema!
      });
      toast.success("Template saved", { id: loader });
      handleCancel();
      await loadTemplates();
    } catch (err) {
      console.error("Failed to save template:", err);
      toast.error("Failed to save template", { id: loader });
      setLoading(false);
    }
  };


  // ── Import JSON Wizard Handlers (Defensive Pre-Validation) ──
  const handleValidateJson = (rawText: string) => {
    setImportJsonText(rawText);
    if (!rawText.trim()) {
      setValidationStatus("empty");
      setSyntaxError(null);
      setSchemaErrors([]);
      return;
    }

    try {
      const parsed = JSON.parse(rawText);

      // 1. Root structure validation
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setValidationStatus("invalid_schema");
        setSyntaxError(null);
        setSchemaErrors(["Root element must be a valid JSON object (cannot be an array or primitive value)."]);
        return;
      }

      // 2. Sections array verification
      if (!parsed.sections) {
        setValidationStatus("invalid_schema");
        setSyntaxError(null);
        setSchemaErrors(["Schema is missing the required 'sections' field."]);
        return;
      }

      if (!Array.isArray(parsed.sections)) {
        setValidationStatus("invalid_schema");
        setSyntaxError(null);
        setSchemaErrors(["The 'sections' field must be a valid JSON array."]);
        return;
      }

      // 3. Strict structural & type check to isolate standard validator from exceptions
      const structuralErrors: string[] = [];
      const validTypes = ["short_text", "long_text", "email", "single_choice", "multi_choice", "dropdown", "yes_no", "target_builder"];

      parsed.sections.forEach((sec: any, si: number) => {
        if (typeof sec !== "object" || sec === null || Array.isArray(sec)) {
          structuralErrors.push(`Section at index ${si} must be a valid JSON object.`);
          return;
        }

        // Section ID validation
        if (sec.id !== undefined && typeof sec.id !== "string") {
          structuralErrors.push(`Section ${si + 1}: 'id' must be a string.`);
        }

        // Title and description type checks to prevent optional chaining type errors
        if (sec.title !== undefined && typeof sec.title !== "string") {
          structuralErrors.push(`Section ${si + 1}: 'title' must be a string value.`);
        }
        if (sec.description !== undefined && typeof sec.description !== "string") {
          structuralErrors.push(`Section "${sec.title || si + 1}": 'description' must be a string.`);
        }

        // Questions array checks
        if (sec.questions !== undefined && !Array.isArray(sec.questions)) {
          structuralErrors.push(`Section "${sec.title || si + 1}": 'questions' must be a JSON array.`);
          return;
        }

        if (sec.questions) {
          sec.questions.forEach((q: any, qi: number) => {
            if (typeof q !== "object" || q === null || Array.isArray(q)) {
              structuralErrors.push(`Section "${sec.title || si + 1}", question at index ${qi} must be a valid JSON object.`);
              return;
            }

            // Question keys & labels type checks
            if (q.qid !== undefined && typeof q.qid !== "string") {
              structuralErrors.push(`Section "${sec.title || si + 1}", question ${qi + 1}: variable key 'qid' must be a string.`);
            }
            if (q.label !== undefined && typeof q.label !== "string") {
              structuralErrors.push(`Section "${sec.title || si + 1}", question ${qi + 1}: 'label' must be a string.`);
            }
            if (q.type !== undefined && typeof q.type !== "string") {
              structuralErrors.push(`Section "${sec.title || si + 1}", question "${q.label || q.qid || qi + 1}": 'type' must be a string.`);
            } else if (q.type !== undefined && !validTypes.includes(q.type)) {
              structuralErrors.push(`Section "${sec.title || si + 1}", question "${q.label || q.qid || qi + 1}": '${q.type}' is an invalid type. Supported: ${validTypes.join(", ")}`);
            }

            // Question required type check
            if (q.required !== undefined && typeof q.required !== "boolean") {
              structuralErrors.push(`Question "${q.label || q.qid || qi + 1}": 'required' must be a boolean.`);
            }

            // Option lists validation
            if (q.options !== undefined && !Array.isArray(q.options)) {
              structuralErrors.push(`Question "${q.label || q.qid || qi + 1}": 'options' must be a JSON array.`);
              return;
            }

            if (q.options) {
              q.options.forEach((opt: any, opti: number) => {
                if (typeof opt !== "string" && (typeof opt !== "object" || opt === null || Array.isArray(opt))) {
                  structuralErrors.push(`Question "${q.label || q.qid || qi + 1}", option at index ${opti} must be a string or a { value, label } object.`);
                  return;
                }
                if (typeof opt === "object") {
                  if (opt.value !== undefined && typeof opt.value !== "string" && typeof opt.value !== "number" && typeof opt.value !== "boolean") {
                    structuralErrors.push(`Question "${q.label || q.qid || qi + 1}", option ${opti + 1}: 'value' must be a string, number, or boolean.`);
                  }
                  if (opt.label !== undefined && typeof opt.label !== "string") {
                    structuralErrors.push(`Question "${q.label || q.qid || qi + 1}", option ${opti + 1}: 'label' must be a string.`);
                  }
                }
              });
            }
          });
        }
      });

      if (structuralErrors.length > 0) {
        setValidationStatus("invalid_schema");
        setSyntaxError(null);
        setSchemaErrors(structuralErrors);
        return;
      }

      // 4. Securely run standard structural scoping schema validation once types are guaranteed
      const errors = validateScopingSchema(parsed);
      if (errors.length > 0) {
        setValidationStatus("invalid_schema");
        setSyntaxError(null);
        setSchemaErrors(errors);
      } else {
        setValidationStatus("valid");
        setSyntaxError(null);
        setSchemaErrors([]);
      }
    } catch (err: any) {
      setValidationStatus("invalid_json");
      setSyntaxError(err?.message || "Invalid JSON syntax formatting");
      setSchemaErrors([]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = (file: File) => {
    if (file.type !== "application/json" && !file.name.endsWith(".json")) {
      toast.error("Please upload a valid JSON (.json) file");
      return;
    }

    // Sanity Limit: 1MB size threshold to prevent memory bloat/hangs
    if (file.size > 1024 * 1024) {
      toast.error("File is too large. Max schema template size is 1MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      handleValidateJson(text);
      toast.success(`Successfully uploaded: ${file.name}`);
    };
    reader.onerror = () => {
      toast.error("Error reading template file");
    };
    reader.readAsText(file);
  };

  const handleCopySample = () => {
    navigator.clipboard.writeText(JSON.stringify(SAMPLE_TEMPLATE_JSON, null, 2));
    toast.success("Sample template JSON copied to clipboard");
  };

  const handleDownloadSample = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(SAMPLE_TEMPLATE_JSON, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", "parent_template_sample.json");
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    toast.success("Sample reference template downloaded");
  };

  const handleApplyImport = () => {
    if (validationStatus !== "valid" || !importJsonText) return;
    try {
      const parsed = JSON.parse(importJsonText);

      // Auto-normalize imported template schema for maximum runtime robustness
      const normalizedSections = (parsed.sections || []).map((sec: any, si: number) => {
        const secId = (typeof sec.id === "string" && sec.id.trim()) 
          ? sec.id.trim() 
          : `section_${Date.now()}_${si}_${Math.random().toString(36).substring(2, 7)}`;
          
        const questions = (sec.questions || []).map((q: any, qi: number) => {
          const qid = (typeof q.qid === "string" && q.qid.trim())
            ? q.qid.trim()
            : `q_${Date.now()}_${si}_${qi}_${Math.random().toString(36).substring(2, 7)}`;
            
          const rawOptions = Array.isArray(q.options) ? q.options : undefined;
          let normalizedOptions: any[] | undefined = undefined;
          if (rawOptions) {
            normalizedOptions = rawOptions.map((opt: any, opti: number) => {
              if (typeof opt === "string") {
                return { value: opt, label: opt };
              }
              if (typeof opt === "object" && opt !== null) {
                return {
                  value: opt.value !== undefined ? String(opt.value) : `option_${opti + 1}`,
                  label: opt.label !== undefined ? String(opt.label) : `Option ${opti + 1}`,
                  flags: Array.isArray(opt.flags) ? opt.flags.map(String) : undefined
                };
              }
              return { value: `option_${opti + 1}`, label: `Option ${opti + 1}` };
            });
          }

          return {
            qid,
            label: typeof q.label === "string" ? q.label.trim() : `Question ${qi + 1}`,
            type: typeof q.type === "string" ? q.type : "short_text",
            required: typeof q.required === "boolean" ? q.required : false,
            ...(normalizedOptions ? { options: normalizedOptions } : {})
          };
        });

        return {
          id: secId,
          title: typeof sec.title === "string" ? sec.title.trim() : `Section ${si + 1}`,
          description: typeof sec.description === "string" ? sec.description.trim() : "",
          questions
        };
      });

      const normalizedSchema = {
        sections: normalizedSections
      };

      editor.resetEditor(normalizedSchema, 0);
      setIsImportModalOpen(false);
      // Reset wizard fields
      setImportJsonText("");
      setValidationStatus("empty");
      toast.success("Template schema imported and normalized successfully");
    } catch {
      toast.error("Failed to apply template schema");
    }
  };

  if (loading && templates.length === 0 && !editingTemplate) {
    return (
      <SalesPageShell>
        <SalesLoading message="Loading templates…" />
      </SalesPageShell>
    );
  }

  return (
    <SalesPageShell>
      <SalesPageHeader
        eyebrow="Form builder"
        title="Questionnaires"
        description="Base schemas for prospect intake. Edits apply to new scoping sessions."
        actions={
          !editingTemplate ? (
            <>
              <SalesLinkButton to="/dashboard/sales" variant="ghost" size="sm">
                Leads
              </SalesLinkButton>
              <SalesButton size="md" onClick={handleStartCreate}>
                <Plus size={14} />
                New template
              </SalesButton>
            </>
          ) : (
            <>
              <SalesLinkButton to="/dashboard/sales" variant="ghost" size="sm">
                Leads
              </SalesLinkButton>
              <SalesButton variant="secondary" onClick={handleCancel}>
                Cancel
              </SalesButton>
              <SalesButton size="md" onClick={() => handleSubmitAttempt()}>
                <Save size={13} />
                Save
              </SalesButton>
            </>
          )
        }
      />

      {editingTemplate ? (
        <form onSubmit={handleSubmitAttempt} className="space-y-4">
          {/* Template Details and Import Trigger */}
          <SalesPanel bodyClassName="p-4 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div className="flex-1">
              <label className="text-[10px] font-medium text-slate-500 block mb-1">
                Template name
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Enterprise intake"
                className={`${salesTokens.input} max-w-md`}
              />
            </div>
            <div className="shrink-0">
              <SalesButton
                variant="secondary"
                onClick={() => setIsImportModalOpen(true)}
                className="flex items-center gap-1 text-[11px]"
              >
                <FileJson size={13} className="text-ctb-blue-600" />
                Import JSON Schema
              </SalesButton>
            </div>
          </SalesPanel>

          <ScopingFormBuilder
            editor={editor}
            templateName={templateName}
            showPreview
            validationErrors={saveErrors}
          />
        </form>

      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tpl) => {
            let numSections = 0;
            let numQuestions = 0;
            try {
              const parsed = JSON.parse(tpl.schema);
              numSections = parsed?.sections?.length || 0;
              numQuestions =
                parsed?.sections?.reduce(
                  (acc: number, sec: { questions?: unknown[] }) =>
                    acc + (sec?.questions?.length || 0),
                  0
                ) || 0;
            } catch {
              /* ignore */
            }

            return (
              <div
                key={tpl.id}
                className={`${salesTokens.panel} p-4 flex flex-col justify-between min-h-[140px] group relative overflow-hidden hover:border-ctb-blue/25 transition`}
              >
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-ctb-blue" />
                <div className="space-y-2 mt-1">
                  <div className="flex justify-between items-start gap-2">
                    <h3 className="text-sm font-bold text-slate-900 line-clamp-1">{tpl.name}</h3>
                    <span className="text-[9px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                      #{tpl.id}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {numSections} sections · {numQuestions} questions
                  </p>
                </div>
                <div className="pt-3 border-t border-slate-100 flex justify-end gap-2 mt-3">
                  {tpl.public_token && (
                    <SalesButton
                      variant="secondary"
                      onClick={() => {
                        const url = `${window.location.origin}/intake/template/${tpl.public_token}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Public link copied!");
                      }}
                      className="flex items-center gap-1"
                    >
                      <Link2 size={12} className="text-[#081549]" />
                      Copy Link
                    </SalesButton>
                  )}
                  <SalesButton variant="secondary" onClick={() => handleStartEdit(tpl)}>
                    <Settings size={12} />
                    Edit
                  </SalesButton>
                </div>
              </div>
            );
          })}

          {templates.length === 0 && (
            <div className={`col-span-full py-14 text-center ${salesTokens.panel}`}>
              <ClipboardCheck size={32} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
              <p className="text-slate-500 text-[11px] font-medium">
                No templates yet. Create one to standardize intake.
              </p>
              <SalesButton className="mt-4 mx-auto" size="md" onClick={handleStartCreate}>
                <Plus size={14} />
                Create template
              </SalesButton>
            </div>
          )}
        </div>
      )}

      {/* ── Premium Fullscreen Double-Column Import JSON Overlay Modal ── */}
      {isImportModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-[4px] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden shadow-2xl border border-slate-200/50 animate-in fade-in zoom-in duration-200">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-ctb-blue/5 rounded-lg border border-ctb-blue/10">
                  <FileJson size={18} className="text-ctb-blue" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Import Template JSON Schema</h3>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Structure a parent template offline and import it instantly.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsImportModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body: Split layout */}
            <div className="flex-1 flex min-h-0 overflow-hidden divide-x divide-slate-100">
              
              {/* Left Column: Guidelines & Reference */}
              <div className="w-1/2 overflow-y-auto p-6 space-y-5 bg-slate-50/30">
                
                {/* Introduction guidelines */}
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide">Schema Guide</h4>
                  <p className="text-[11px] leading-relaxed text-slate-600">
                    A valid template schema is a JSON object composed of an array of <strong>sections</strong>. Each section holds a collection of <strong>questions</strong> mapped to their variable database keys.
                  </p>
                </div>

                {/* Supported Types List */}
                <div className="space-y-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <h5 className="text-[10px] font-bold text-slate-700 uppercase tracking-wide">Supported Question Types</h5>
                  <div className="grid grid-cols-2 gap-2.5 text-[10px]">
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">short_text</span>
                      <span className="text-slate-500 text-[9px]">Single-line raw text inputs.</span>
                    </div>
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">long_text</span>
                      <span className="text-slate-500 text-[9px]">Multi-line paragraph text areas.</span>
                    </div>
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">email</span>
                      <span className="text-slate-500 text-[9px]">RFC-validated email fields.</span>
                    </div>
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">yes_no</span>
                      <span className="text-slate-500 text-[9px]">Two-state Boolean toggle.</span>
                    </div>
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">dropdown</span>
                      <span className="text-slate-500 text-[9px]">Select menus (requires options array).</span>
                    </div>
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">multi_choice</span>
                      <span className="text-slate-500 text-[9px]">Checkbox groups (requires options).</span>
                    </div>
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">single_choice</span>
                      <span className="text-slate-500 text-[9px]">Radio button arrays (requires options).</span>
                    </div>
                    <div className="p-2 bg-slate-50/50 rounded-lg border border-slate-100">
                      <span className="font-semibold text-ctb-blue block font-mono">target_builder</span>
                      <span className="text-slate-500 text-[9px]">Specialized scoping scoping list builder.</span>
                    </div>
                  </div>
                </div>

                {/* Sample Code Panel */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h5 className="text-[10px] font-bold text-slate-700 uppercase tracking-wide">Sample Reference JSON</h5>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleCopySample}
                        className="inline-flex items-center gap-1 text-[9px] font-bold text-slate-600 bg-white border border-slate-200 px-2 py-1 rounded hover:bg-slate-50 transition"
                      >
                        <ClipboardCopy size={10} />
                        Copy
                      </button>
                      <button
                        onClick={handleDownloadSample}
                        className="inline-flex items-center gap-1 text-[9px] font-bold text-ctb-blue bg-ctb-blue/5 border border-ctb-blue/10 px-2 py-1 rounded hover:bg-ctb-blue/10 transition"
                      >
                        <Download size={10} />
                        Download .json
                      </button>
                    </div>
                  </div>
                  <div className="relative rounded-xl border border-slate-200 overflow-hidden shadow-inner">
                    <pre className="p-4 bg-slate-900 text-slate-200 font-mono text-[9px] leading-relaxed max-h-[220px] overflow-y-auto">
                      {JSON.stringify(SAMPLE_TEMPLATE_JSON, null, 2)}
                    </pre>
                  </div>
                </div>

              </div>

              {/* Right Column: Paste, Drag-and-Drop, Live Visual Validation */}
              <div className="w-1/2 flex flex-col min-h-0 bg-white p-6">
                
                {/* Drag-and-Drop Area */}
                <div
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-xl p-4 text-center transition flex flex-col items-center justify-center mb-4 ${
                    dragActive ? "border-ctb-blue bg-ctb-blue/5 text-ctb-blue" : "border-slate-200 hover:border-slate-300 text-slate-500"
                  }`}
                >
                  <UploadCloud size={28} className={dragActive ? "text-ctb-blue animate-bounce" : "text-slate-300"} />
                  <p className="text-[10px] font-bold mt-1.5">
                    Drag and drop your template JSON file here
                  </p>
                  <p className="text-[9px] text-slate-400 mt-0.5">
                    or
                  </p>
                  <label className="mt-1.5 inline-flex items-center justify-center font-bold text-xs bg-slate-50 border border-slate-200 px-3 py-1 rounded-md text-slate-700 cursor-pointer hover:bg-slate-100 transition">
                    Browse File
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Paste Area label */}
                <div className="flex justify-between items-center mb-1">
                  <label className="text-[10px] font-bold text-slate-700 uppercase tracking-wide">
                    Paste raw JSON string
                  </label>
                  {importJsonText && (
                    <span className="text-[9px] font-medium text-slate-400 font-mono">
                      {importJsonText.length} chars
                    </span>
                  )}
                </div>

                {/* Text Area */}
                <div className="flex-1 min-h-0 relative rounded-xl border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-ctb-blue/10 focus-within:border-ctb-blue transition mb-4">
                  <textarea
                    value={importJsonText}
                    onChange={(e) => handleValidateJson(e.target.value)}
                    placeholder='Pasted JSON e.g. { "sections": [...] }'
                    className="w-full h-full p-3 font-mono text-[10px] text-slate-800 placeholder:text-slate-400 resize-none focus:outline-none"
                  />
                </div>

                {/* Live Visual Validation alerts */}
                <div className="shrink-0 mb-4">
                  {validationStatus === "empty" && (
                    <div className="p-3 bg-slate-50 border border-slate-200 text-slate-500 rounded-xl flex gap-2 items-start">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      <div>
                        <div className="text-[10px] font-bold">Awaiting Schema Input</div>
                        <p className="text-[9px] mt-0.5 leading-relaxed">
                          Paste raw JSON or drop a file in the slot above to trigger live validation diagnostics.
                        </p>
                      </div>
                    </div>
                  )}

                  {validationStatus === "invalid_json" && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-xl flex gap-2 items-start">
                      <AlertCircle size={14} className="mt-0.5 shrink-0 text-red-500" />
                      <div>
                        <div className="text-[10px] font-bold text-red-700">Invalid JSON Syntax</div>
                        <p className="text-[9px] font-mono mt-1 leading-relaxed bg-red-100/50 p-1.5 rounded border border-red-200/50 overflow-x-auto max-w-full">
                          {syntaxError}
                        </p>
                      </div>
                    </div>
                  )}

                  {validationStatus === "invalid_schema" && (
                    <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl flex gap-2 items-start">
                      <AlertCircle size={14} className="mt-0.5 shrink-0 text-amber-500" />
                      <div className="w-full">
                        <div className="text-[10px] font-bold text-amber-800">Structural Schema Errors</div>
                        <ul className="text-[9px] mt-1.5 space-y-1 list-disc pl-4 max-h-[100px] overflow-y-auto leading-relaxed">
                          {schemaErrors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {validationStatus === "valid" && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl flex gap-2 items-start">
                      <CheckCircle size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                      <div>
                        <div className="text-[10px] font-bold text-emerald-800">Schema is fully valid!</div>
                        <p className="text-[9px] mt-0.5 leading-relaxed text-emerald-700">
                          Found exactly <strong>
                            {(() => {
                              try {
                                return JSON.parse(importJsonText)?.sections?.length || 0;
                              } catch {
                                return 0;
                              }
                            })()} sections
                          </strong> and <strong>
                            {(() => {
                              try {
                                return JSON.parse(importJsonText)?.sections?.reduce(
                                  (acc: number, sec: any) => acc + (sec?.questions?.length || 0),
                                  0
                                ) || 0;
                              } catch {
                                return 0;
                              }
                            })()} questions
                          </strong>. Ready to import.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer Controls */}
                <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2.5">
                  <SalesButton variant="secondary" onClick={() => setIsImportModalOpen(false)}>
                    Close
                  </SalesButton>
                  <SalesButton
                    variant={validationStatus === "valid" ? "success" : "primary"}
                    disabled={validationStatus !== "valid"}
                    onClick={handleApplyImport}
                  >
                    Apply Template Schema
                  </SalesButton>
                </div>

              </div>

            </div>

          </div>
        </div>,
        document.body
      )}

      <TemplateDevAutofill
        editingTemplate={editingTemplate}
        setEditingTemplate={setEditingTemplate}
        setTemplateName={setTemplateName}
        schemaEditor={editor}
      />

      {/* ── Confirmation Modal ── */}
      {showConfirmSaveModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white border border-slate-200/80 rounded-2xl shadow-2xl p-6 text-slate-800 relative overflow-hidden flex flex-col animate-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-ctb-blue/10 flex items-center justify-center shrink-0 border border-ctb-blue/20">
                <Save size={20} className="text-ctb-blue" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 leading-tight">Save Questionnaire</h3>
                <p className="text-[10px] text-slate-500 mt-0.5">Please confirm template changes</p>
              </div>
            </div>
            
            <div className="space-y-2.5 bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6 text-xs text-slate-600">
              <div className="flex justify-between">
                <span className="font-medium text-slate-400 text-[10px] uppercase">Template Name:</span>
                <span className="font-bold text-slate-800">{templateName}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-slate-400 text-[10px] uppercase">Sections:</span>
                <span className="font-semibold text-slate-700">{editor.schema?.sections?.length || 0} sections</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-slate-400 text-[10px] uppercase">Total Questions:</span>
                <span className="font-semibold text-slate-700">
                  {editor.schema?.sections?.reduce((acc: number, sec: any) => acc + (sec.questions?.length || 0), 0) || 0} questions
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-650 mb-6 leading-relaxed">
              Are you sure you want to save this Questionnaire? All active and future Questionnaires matching this template's schema format will reflect these updates.
            </p>

            <div className="flex justify-end gap-2.5">
              <button
                type="button"
                onClick={() => setShowConfirmSaveModal(false)}
                className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-xs font-semibold transition active:scale-98"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSave}
                className="px-5 py-2 bg-[#081549] hover:bg-[#081549]/90 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-98"
              >
                Confirm &amp; Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </SalesPageShell>
  );
};

export default QuestionnaireTemplatesPage;

